import { isTenantConfig, type TenantConfig } from '@pink/shared';

/**
 * Pfad, unter dem Nginx pro Domain die Tenant-Config ausliefert.
 */
const TENANT_CONFIG_URL = '/api/config.json';

/**
 * Fehlerklassen für den Bootstrap-Fetch — diskriminieren Netzwerk- und
 * Schema-Probleme, damit `<ConfigProvider>` und (in DEV-016) der
 * Technical-Error-Screen passend reagieren können.
 */
export const ConfigFetchErrorKind = {
  Network: 'network',
  Schema: 'schema',
} as const;

export type ConfigFetchErrorKind = (typeof ConfigFetchErrorKind)[keyof typeof ConfigFetchErrorKind];

export class ConfigFetchError extends Error {
  public readonly kind: ConfigFetchErrorKind;

  public constructor(kind: ConfigFetchErrorKind, message: string) {
    super(message);
    this.name = 'ConfigFetchError';
    this.kind = kind;
  }
}

/**
 * Lädt und validiert die Tenant-Config vom Nginx-Endpunkt.
 *
 * Network-Fehler (Fetch-Ablehnung, non-2xx-Status) werden zu
 * `ConfigFetchError(kind: 'network')`, Schema-Mismatch zu
 * `ConfigFetchError(kind: 'schema')` zusammengefasst — der `<ConfigProvider>`
 * setzt darauf seinen Bootstrap-Error-State.
 *
 * @param signal Optionales AbortSignal — der Caller kann den Fetch abbrechen.
 */
export async function fetchTenantConfig(signal?: AbortSignal): Promise<TenantConfig> {
  const response = await safeFetch(signal);
  const payload = await safeReadJson(response);
  if (!isTenantConfig(payload)) {
    throw new ConfigFetchError(
      ConfigFetchErrorKind.Schema,
      'Tenant-Config entspricht nicht dem erwarteten Schema.',
    );
  }
  return payload;
}

async function safeFetch(signal?: AbortSignal): Promise<Response> {
  let response: Response;
  const init: RequestInit =
    signal === undefined ? { credentials: 'omit' } : { credentials: 'omit', signal };
  try {
    response = await fetch(TENANT_CONFIG_URL, init);
  } catch (cause) {
    throw new ConfigFetchError(
      ConfigFetchErrorKind.Network,
      `Tenant-Config konnte nicht geladen werden: ${describeCause(cause)}`,
    );
  }
  if (!response.ok) {
    throw new ConfigFetchError(
      ConfigFetchErrorKind.Network,
      `Tenant-Config-Endpunkt antwortete mit HTTP ${response.status}.`,
    );
  }
  return response;
}

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch (cause) {
    throw new ConfigFetchError(
      ConfigFetchErrorKind.Schema,
      `Tenant-Config ist kein gültiges JSON: ${describeCause(cause)}`,
    );
  }
}

function describeCause(cause: unknown): string {
  if (cause instanceof Error) {
    return cause.message;
  }
  return 'unbekannter Fehler';
}
